import { createContext, useContext, useState } from "react";

const translations = {
  en: {
    label: "EN",
    nav: { work: "Work", studio: "Studio", contact: "Contact", cta: "Start a project" },
    hero: {
      badge: "Motion-first creative studio",
      l1: "MOTION", l2: "IS THE NEW", l3: "ADVERTISING.",
      desc: "Onyqx crafts scroll-stopping motion ads for brands that refuse to blend in. Concepts, animation and delivery — all under one roof.",
      scroll: "Scroll to view work",
    },
    work: {
      eyebrow: "Selected concepts", heading: "MOTION IN FOCUS.",
      helper: "Hover any reel to preview — click to play with sound. Use the speaker icon to mute.",
      empty: "No ads yet. Unlock the admin panel to upload your reels.",
    },
    manifesto: {
      eyebrow: "The studio", heading: "WHAT WE BELIEVE.",
      chapters: [
        { title: "Motion is Emotion", body: "A static frame informs. A moving one persuades. We design every beat to make people feel before they think." },
        { title: "Frame-by-Frame Precision", body: "No lazy transitions, no stock templates. Every keyframe is intentional — timed, eased and pixel-checked." },
        { title: "Built to Stop the Scroll", body: "Ads that earn the first three seconds. We engineer hooks that hold attention where attention is scarce." },
      ],
    },
    contact: {
      eyebrow: "Let's talk", heading1: "AVAIL OUR", heading2: "SERVICES.",
      desc: "Have a product, a launch or a wild idea? Send it over using the form and let's put it in motion.",
      name: "Your name", email: "Email address", projectType: "Project type",
      message: "Tell us about your project", send: "Send message",
      projectTypes: ["Product Reveal", "Brand Film", "Social Ad Series", "Full Campaign"],
      okTitle: "Message sent!", okDesc: "We'll be in touch within 24 hours.",
      errFill: "Please fill in your name, email and message.", errGeneric: "Something went wrong. Please try again.",
    },
    footer: { agency: "Onyqx Motion Ad Agency", reach: "Reach us through the contact form above.", rights: "All rights reserved.", tagline: "Motion-first creative studio" },
  },
  es: {
    label: "ES",
    nav: { work: "Trabajos", studio: "Estudio", contact: "Contacto", cta: "Iniciar proyecto" },
    hero: {
      badge: "Estudio creativo centrado en el movimiento",
      l1: "EL MOVIMIENTO", l2: "ES LA NUEVA", l3: "PUBLICIDAD.",
      desc: "Onyqx crea anuncios en movimiento que detienen el scroll para marcas que se niegan a pasar desapercibidas. Concepto, animación y entrega — todo bajo un mismo techo.",
      scroll: "Desplázate para ver el trabajo",
    },
    work: {
      eyebrow: "Conceptos seleccionados", heading: "MOVIMIENTO EN FOCO.",
      helper: "Pasa el cursor para previsualizar; haz clic para reproducir con sonido. Usa el altavoz para silenciar.",
      empty: "Aún no hay anuncios. Desbloquea el panel de administración para subir tus vídeos.",
    },
    manifesto: {
      eyebrow: "El estudio", heading: "EN QUÉ CREEMOS.",
      chapters: [
        { title: "El movimiento es emoción", body: "Un fotograma estático informa. Uno en movimiento persuade. Diseñamos cada momento para que la gente sienta antes de pensar." },
        { title: "Precisión fotograma a fotograma", body: "Sin transiciones perezosas ni plantillas genéricas. Cada fotograma clave es intencional — cronometrado y revisado al píxel." },
        { title: "Hecho para frenar el scroll", body: "Anuncios que ganan los primeros tres segundos. Diseñamos ganchos que retienen la atención donde escasea." },
      ],
    },
    contact: {
      eyebrow: "Hablemos", heading1: "SOLICITA", heading2: "SERVICIOS.",
      desc: "¿Tienes un producto, un lanzamiento o una idea audaz? Envíala con el formulario y pongámosla en movimiento.",
      name: "Tu nombre", email: "Correo electrónico", projectType: "Tipo de proyecto",
      message: "Cuéntanos sobre tu proyecto", send: "Enviar mensaje",
      projectTypes: ["Lanzamiento de producto", "Film de marca", "Serie para redes", "Campaña completa"],
      okTitle: "¡Mensaje enviado!", okDesc: "Nos pondremos en contacto en 24 horas.",
      errFill: "Completa tu nombre, correo y mensaje.", errGeneric: "Algo salió mal. Inténtalo de nuevo.",
    },
    footer: { agency: "Onyqx Agencia de Anuncios en Movimiento", reach: "Contáctanos mediante el formulario de arriba.", rights: "Todos los derechos reservados.", tagline: "Estudio centrado en el movimiento" },
  },
  fr: {
    label: "FR",
    nav: { work: "Projets", studio: "Studio", contact: "Contact", cta: "Démarrer un projet" },
    hero: {
      badge: "Studio créatif axé sur le mouvement",
      l1: "LE MOUVEMENT", l2: "EST LA NOUVELLE", l3: "PUBLICITÉ.",
      desc: "Onyqx conçoit des publicités animées qui captent l'attention pour les marques qui refusent de se fondre dans la masse. Concept, animation et livraison — le tout sous un même toit.",
      scroll: "Faites défiler pour voir les projets",
    },
    work: {
      eyebrow: "Concepts sélectionnés", heading: "LE MOUVEMENT EN GROS PLAN.",
      helper: "Survolez pour prévisualiser ; cliquez pour lire avec le son. Utilisez le haut-parleur pour couper le son.",
      empty: "Aucune publicité pour l'instant. Déverrouillez le panneau d'administration pour ajouter vos vidéos.",
    },
    manifesto: {
      eyebrow: "Le studio", heading: "CE EN QUOI NOUS CROYONS.",
      chapters: [
        { title: "Le mouvement, c'est l'émotion", body: "Une image fixe informe. Une image en mouvement persuade. Nous concevons chaque instant pour faire ressentir avant de faire réfléchir." },
        { title: "Précision image par image", body: "Pas de transitions paresseuses ni de modèles génériques. Chaque image-clé est intentionnelle — minutée et vérifiée au pixel près." },
        { title: "Conçu pour stopper le défilement", body: "Des publicités qui gagnent les trois premières secondes. Nous créons des accroches qui retiennent l'attention là où elle est rare." },
      ],
    },
    contact: {
      eyebrow: "Discutons", heading1: "PROFITEZ DE", heading2: "NOS SERVICES.",
      desc: "Un produit, un lancement ou une idée folle ? Envoyez-le via le formulaire et mettons-le en mouvement.",
      name: "Votre nom", email: "Adresse e-mail", projectType: "Type de projet",
      message: "Parlez-nous de votre projet", send: "Envoyer le message",
      projectTypes: ["Révélation produit", "Film de marque", "Série réseaux sociaux", "Campagne complète"],
      okTitle: "Message envoyé !", okDesc: "Nous vous répondrons sous 24 heures.",
      errFill: "Veuillez renseigner votre nom, e-mail et message.", errGeneric: "Une erreur s'est produite. Réessayez.",
    },
    footer: { agency: "Onyqx Agence de Publicité en Mouvement", reach: "Contactez-nous via le formulaire ci-dessus.", rights: "Tous droits réservés.", tagline: "Studio axé sur le mouvement" },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("onyqx_lang") || "en");
  const setLang = (l) => { localStorage.setItem("onyqx_lang", l); setLangState(l); };
  const t = translations[lang] || translations.en;
  const languages = Object.keys(translations).map((k) => ({ code: k, label: translations[k].label }));
  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
